import "./utils/configs";
import { isValidSceneUrl } from "./utils/scene-url-utils";
import { SOUND_CHAT_MESSAGE } from "./systems/sound-effects-system";
import { EventTarget } from "event-target-shim";
import { ExitReason } from "./react-components/room/ExitedRoomScreen";
import { LogMessageType } from "./utils/messages";
import { createNetworkedEntity } from "./utils/create-networked-entity";
import { add, testAsset, respawn } from "./utils/chat-commands";
import { isLockedDownDemoRoom } from "./utils/hub-utils";
import nextTick from "./utils/next-tick";
import NotificationMessage from "./utils/notification-messages";
import configManager from "./singleton/ConfigManager";
import { CHAT_DELIVERY_TRANSPORT, dispatchPublicChatMessage } from "./utils/chat-transport";

let uiRoot;
// Handles user-entered messages
export default class MessageDispatch extends EventTarget {
  constructor(scene, entryManager, hubChannel, remountUI, mediaSearchStore) {
    super();
    this.scene = scene;
    this.entryManager = entryManager;
    this.hubChannel = hubChannel;
    this.remountUI = remountUI;
    this.mediaSearchStore = mediaSearchStore;
    this.presenceLogEntries = [];
    this.mySessionId = null;
    this.notificationMessage = new NotificationMessage();
  }

  addToPresenceLog(entry) {
    entry.key = Date.now().toString();
    if (
      (entry.type === "join" ||
        entry.type === "leave" ||
        entry.type === "chat" ||
        entry.type === "entered" ||
        entry.type === "photo" ||
        entry.type === "video") &&
      !this.scene.is("entered")
    ) {
      return;
    }

    if (entry && entry.type == "change_scene" && entry?.body?.smoothTransition) {
      window.smoothTransition = entry?.body?.smoothTransition;
    }

    // disable join and leave notifications if they are not enabled
    let sendNotification = !!configManager.config.avatarNetworking.enabled;

    const sessionId = this.mySessionId || window.mySessionId;
    this.notificationMessage.mySessionId = sessionId;

    if (entry.type === "chat" && entry.body?.privacyType === "private-chat" && entry.body?.receiver !== sessionId) {
      sendNotification = false;
    }

    if (sendNotification) {
      this.notificationMessage.pushNotification(entry);
    }

    if (entry.type === "chat" && this.scene.is("loaded")) {
      if (!entry.body?.privacyType || entry?.body?.receiver === sessionId) {
        this.scene.systems["hubs-systems"].soundEffectsSystem.playSoundOneShot(SOUND_CHAT_MESSAGE);
      }
    }

    // Check Instant Try Teleport to Waypoint on exit needed?
    if (entry.type == "leave") {
      try {
        const { waypointSystem, characterController } = this.scene.systems["hubs-systems"];
        const avatarAnim = characterController.avatarRig.querySelector("[avatar-animation]");
        const isSit = avatarAnim?.components["avatar-animation"]?.isSit();
        const currentWaypoint = waypointSystem.currentWaypoint;
        nextTick().then(() => {
          if (currentWaypoint && isSit) {
            waypointSystem.releaseAnyOccupiedWaypoints();
            waypointSystem.instantTryTeleportToOccupiableWaypoint(currentWaypoint);
          } else {
            waypointSystem.releaseAnyOccupiedWaypoints();
          }
        });
      } catch (error) {
        console.error("Error handling leave event:", error);
      }
    }

    // Fade out and then remove
    setTimeout(() => {
      entry.expired = true;
      this.remountUI({ presenceLogEntries: this.presenceLogEntries });

      setTimeout(() => {
        this.presenceLogEntries.splice(this.presenceLogEntries.indexOf(entry), 1);
        this.remountUI({ presenceLogEntries: this.presenceLogEntries });
      }, 1000);
    }, 20000);
  }

  receive(message) {
    if (isLockedDownDemoRoom()) return;

    this.addToPresenceLog(message);
    this.dispatchEvent(new CustomEvent("message", { detail: message }));
  }

  log = (messageType, props) => {
    this.receive({ type: "log", messageType, props });
  };

  shortcutLog = (messageType, props) => {
    if (configManager.config.shortcuts.enabled) {
      this.receive({ type: "log", messageType, props });
    }
  };

  push = (body, type) => this.hubChannel.sendMessage(body, type);

  dispatch = message => {
    if (message.startsWith("/")) {
      const commandParts = message.substring(1).split(/\s+/);
      this.dispatchCommand(commandParts[0], ...commandParts.slice(1));
      document.activeElement.blur(); // Commands should blur
      return CHAT_DELIVERY_TRANSPORT.NONE;
    } else {
      return dispatchPublicChatMessage({ message, hubChannel: this.hubChannel });
    }
  };

  dispatchCommand = async (command, ...args) => {
    const entered = this.scene.is("entered");
    uiRoot = uiRoot || document.querySelector(".ui-root");
    const isGhost = !entered && uiRoot && uiRoot.classList.contains("isGhost");

    // TODO: Some of the commands below should be available without requiring room entry.
    if (!entered && !isGhost) {
      this.log(LogMessageType.roomEntryRequired);
      return;
    }

    const avatarRig = document.querySelector("#avatar-rig");
    const scales = [0.0625, 0.125, 0.25, 0.5, 1.0, 1.5, 3, 5, 7.5, 12.5];
    const curScale = avatarRig.object3D.scale;
    let err;
    let physicsSystem;
    const captureSystem = this.scene.systems["capture-system"];

    switch (command) {
      case "fly":
        if (this.scene.systems["hubs-systems"].characterController.fly) {
          this.scene.systems["hubs-systems"].characterController.enableFly(false);
          this.shortcutLog(LogMessageType.flyModeDisabled);
        } else {
          if (this.scene.systems["hubs-systems"].characterController.enableFly(true)) {
            this.shortcutLog(LogMessageType.flyModeEnabled);
          }
        }
        break;
      case "grow":
        for (let i = 0; i < scales.length; i++) {
          if (scales[i] > curScale.x) {
            avatarRig.object3D.scale.set(scales[i], scales[i], scales[i]);
            avatarRig.object3D.matrixNeedsUpdate = true;
            break;
          }
        }

        break;
      case "shrink":
        for (let i = scales.length - 1; i >= 0; i--) {
          if (curScale.x > scales[i]) {
            avatarRig.object3D.scale.set(scales[i], scales[i], scales[i]);
            avatarRig.object3D.matrixNeedsUpdate = true;
            break;
          }
        }

        break;
      case "leave":
        this.entryManager.exitScene();
        this.remountUI({ roomUnavailableReason: ExitReason.left });
        break;
      case "cube": {
        const avatarPov = document.querySelector("#avatar-pov-node").object3D;
        const eid = createNetworkedEntity(APP.world, "cube");
        const obj = APP.world.eid2obj.get(eid);
        obj.position.copy(avatarPov.localToWorld(new THREE.Vector3(0, 0, -1.5)));
        obj.lookAt(avatarPov.getWorldPosition(new THREE.Vector3()));
        break;
      }
      case "debug":
        physicsSystem = document.querySelector("a-scene").systems["hubs-systems"].physicsSystem;
        physicsSystem.setDebug(!physicsSystem.debugEnabled);
        break;
      case "vrstats":
        document.getElementById("stats").components["stats-plus"].toggleVRStats();
        break;
      case "scene":
        if (args[0]) {
          if (await isValidSceneUrl(args[0])) {
            err = this.hubChannel.updateScene(args[0]);
            if (err === "unauthorized") {
              this.log(LogMessageType.unauthorizedSceneChange);
            }
          } else {
            this.log(LogMessageType.invalidSceneUrl);
          }
        } else if (this.hubChannel.canOrWillIfCreator("update_hub")) {
          this.mediaSearchStore.sourceNavigateWithNoNav("scenes", "use");
        }

        break;
      case "rename":
        err = this.hubChannel.rename(args.join(" "));
        if (err === "unauthorized") {
          this.log(LogMessageType.unauthorizedRoomRename);
        }
        break;
      case "capture":
        if (!captureSystem.available()) {
          this.log(LogMessageType.captureUnavailable);
          break;
        }
        if (args[0] === "stop") {
          if (captureSystem.started()) {
            captureSystem.stop();
            this.log(LogMessageType.captureStopped);
          } else {
            this.log(LogMessageType.captureAlreadyStopped);
          }
        } else {
          if (captureSystem.started()) {
            this.log(LogMessageType.captureAlreadyRunning);
          } else {
            captureSystem.start();
            this.log(LogMessageType.captureStarted);
          }
        }
        break;
      case "audioNormalization":
        {
          if (args.length === 1) {
            const factor = Number(args[0]);
            if (!isNaN(factor)) {
              const effectiveFactor = Math.max(0.0, Math.min(255.0, factor));
              window.APP.store.update({
                preferences: { audioNormalization: effectiveFactor }
              });
              if (factor) {
                this.log(LogMessageType.setAudioNormalizationFactor, { factor: effectiveFactor });
              } else {
                this.log(LogMessageType.audioNormalizationDisabled);
              }
            } else {
              this.log(LogMessageType.audioNormalizationNaN);
            }
          } else {
            this.log(LogMessageType.invalidAudioNormalizationRange);
          }
        }
        break;
      case "add":
        {
          const avatarPov = document.querySelector("#avatar-pov-node").object3D;
          add(APP.world, avatarPov, args);
        }
        break;
      case "respawn":
        {
          const sceneEl = AFRAME.scenes[0];
          const characterController = this.scene.systems["hubs-systems"].characterController;
          respawn(APP.world, sceneEl, characterController);
        }
        break;
      case "test":
        {
          const avatarPov = document.querySelector("#avatar-pov-node").object3D;
          testAsset(APP.world, avatarPov, args);
        }
        break;
    }
  };
}
